import { useEffect, useState } from "react";
import { AlertTriangle, ExternalLink, ImageIcon, LayoutGrid, Loader2, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, assetUrl, formatApiErrorDetail } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { PortfolioDialog } from "@/components/admin/PortfolioDialog";
import { ConfirmDelete } from "@/components/admin/shared";
import { AdminHeader, EmptyState } from "./AdminLayout";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

const Thumb = ({ item }) => (
  <div className="relative aspect-[16/10] w-28 shrink-0 overflow-hidden rounded-lg bg-white/5">
    {item.image_src ? <img src={assetUrl(item.image_src)} alt={item.name} className="h-full w-full object-cover" />
      : <ImageIcon className="absolute inset-0 m-auto h-5 w-5 text-slate-600" />}
    {item.image_status === "generating" && <div className="absolute inset-0 grid place-items-center bg-black/60"><Loader2 className="h-5 w-5 animate-spin text-cyan-300" /></div>}
  </div>
);

const PhotoStatus = ({ item }) => {
  if (item.image_status === "generating")
    return <p data-testid={`portfolio-photo-status-${item.id}`} className="mt-1 inline-flex items-center gap-1.5 text-xs text-cyan-300"><Loader2 className="h-3 w-3 animate-spin" /> Generating photo…</p>;
  if (item.image_status === "failed")
    return <p data-testid={`portfolio-photo-status-${item.id}`} className="mt-1 inline-flex items-center gap-1.5 text-xs text-amber-300"><AlertTriangle className="h-3 w-3" /> {item.image_error || "Couldn't generate photo — try Refresh photo"}</p>;
  return null;
};

const Row = ({ item, onToggle, onEdit, onDelete, onRefresh }) => (
  <div data-testid={`admin-portfolio-row-${item.id}`} className="flex flex-col gap-4 border-b border-white/5 p-4 last:border-0 sm:flex-row sm:items-center">
    <Thumb item={item} />
    <div className="min-w-0 flex-1">
      <p className="font-medium text-white">{item.name}</p>
      <a href={item.url} target="_blank" rel="noopener noreferrer" className="inline-flex max-w-full items-center gap-1 truncate text-xs text-cyan-300/80 hover:text-cyan-200">
        <span className="truncate">{item.url}</span> <ExternalLink className="h-3 w-3 shrink-0" />
      </a>
      <p className="mt-1 line-clamp-1 text-xs text-slate-500">{item.description}</p>
      <PhotoStatus item={item} />
    </div>
    <div className="flex flex-wrap items-center gap-3">
      <button onClick={() => onRefresh(item)} disabled={item.image_status === "generating"} data-testid={`portfolio-refresh-photo-${item.id}`}
        className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-cyan-400/30 px-3 py-1.5 text-xs text-cyan-300 hover:bg-cyan-400/10 disabled:cursor-not-allowed disabled:opacity-40">
        <RefreshCw className={`h-3 w-3 ${item.image_status === "generating" ? "animate-spin" : ""}`} /> Refresh photo
      </button>
      <label className="flex items-center gap-2 text-xs text-slate-400">
        <Switch checked={item.visible} onCheckedChange={(v) => onToggle(item, v)} data-testid={`portfolio-visible-toggle-${item.id}`} />
        <span data-testid={`portfolio-visibility-label-${item.id}`}>{item.visible ? "Visible" : "Hidden"}</span>
      </label>
      <button onClick={() => onEdit(item)} data-testid={`portfolio-edit-button-${item.id}`} aria-label="Edit" className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-cyan-300"><Pencil className="h-4 w-4" /></button>
      <button onClick={() => onDelete(item)} data-testid={`portfolio-delete-button-${item.id}`} aria-label="Delete" className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
    </div>
  </div>
);

export default function PortfolioManager() {
  const [items, setItems] = useState(null);
  const [dialog, setDialog] = useState({ open: false, item: null });
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/admin/portfolio").then((r) => setItems(r.data)).catch((e) => { setItems([]); err(e); });
  useEffect(() => { load(); }, []);

  const generating = (items || []).some((i) => i.image_status === "generating");
  useEffect(() => {
    if (!generating) return undefined;
    const t = setInterval(() => api.get("/admin/portfolio").then((r) => setItems(r.data)).catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [generating]);

  const refresh = async (item) => {
    try {
      const { data } = await api.post(`/admin/portfolio/${item.id}/refresh-image`);
      setItems((xs) => xs.map((x) => (x.id === data.id ? data : x)));
      toast.success(`Generating a new photo for ${item.name}`);
    } catch (e) { err(e); }
  };

  const save = async (payload) => {
    if (dialog.item) await api.put(`/admin/portfolio/${dialog.item.id}`, payload);
    else await api.post("/admin/portfolio", payload);
    toast.success(dialog.item ? "Website updated" : "Website added");
    setDialog({ open: false, item: null });
    load();
  };

  const toggle = async (item, visible) => {
    try {
      const { id, created_at, ...rest } = item;
      await api.put(`/admin/portfolio/${id}`, { ...rest, visible });
      setItems((xs) => xs.map((x) => (x.id === id ? { ...x, visible } : x)));
      toast.success(visible ? `${item.name} is now public` : `${item.name} hidden`);
    } catch (e) { err(e); }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/portfolio/${toDelete.id}`);
      toast.success("Website deleted");
      setToDelete(null);
      load();
    } catch (e) { err(e); }
  };

  const add = (
    <button onClick={() => setDialog({ open: true, item: null })} data-testid="admin-add-portfolio-button"
      className="btn-glow inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2.5 text-sm font-semibold text-[#07090E] hover:bg-cyan-300">
      <Plus className="h-4 w-4" /> Add website
    </button>
  );

  return (
    <div data-testid="admin-portfolio-page">
      <AdminHeader eyebrow="// public" title="Portfolio" action={add}>Websites shown on the public Portfolio page.</AdminHeader>
      {items === null ? <div className="h-40 animate-pulse rounded-2xl bg-white/[0.03]" /> : items.length === 0 ? (
        <EmptyState icon={LayoutGrid} title="No portfolio websites" text="Add your first site. The public page shows 'New projects coming soon' until then." testId="admin-portfolio-empty-state" />
      ) : (
        <div data-testid="admin-portfolio-table" className="rounded-2xl border border-white/5 bg-[#0B0F18]">
          {items.map((it) => <Row key={it.id} item={it} onToggle={toggle} onEdit={(i) => setDialog({ open: true, item: i })} onDelete={setToDelete} onRefresh={refresh} />)}
        </div>
      )}
      <PortfolioDialog open={dialog.open} item={dialog.item} onClose={() => setDialog({ open: false, item: null })} onSave={save} />
      <ConfirmDelete target={toDelete} label={toDelete?.name} onCancel={() => setToDelete(null)} onConfirm={remove} />
    </div>
  );
}
